import i18n, { type BackendModule } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

/**
 * i18n setup — bilingual (NL + EN) per REQUIREMENTS.md §3.4.
 *
 * Default: browser locale → NL maps to NL, anything else falls back to EN.
 * Per-user override: stored in localStorage key `priceq.lang` and surfaced in
 * profile settings (page lands when settings module is built).
 *
 * Both locale files MUST stay in sync — CI-script `audit-locales.mjs` fails
 * the build on missing keys.
 *
 * Locales are NOT bundled into the main chunk: each one is its own lazy chunk
 * (~100 KB minified each) and is only fetched when i18next asks for it — the
 * active language plus the `en` fallback. Switching language loads the new
 * one on demand; `i18n.changeLanguage()` resolves once it is in.
 */
const loaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  de: () => import('./locales/de.json'),
  en: () => import('./locales/en.json'),
  fr: () => import('./locales/fr.json'),
  nl: () => import('./locales/nl.json'),
  pl: () => import('./locales/pl.json'),
};

const lazyBackend: BackendModule = {
  type: 'backend',
  init() {},
  read(lng, _ns, cb) {
    const load = loaders[lng];
    // supportedLngs keeps unknown codes out; an empty bundle (not an error)
    // avoids i18next's retry loop should one slip through.
    if (!load) return cb(null, {});
    load().then((m) => cb(null, m.default), (err: unknown) => cb(err as Error, null));
  },
};

/** `querystring` first, and deliberately ahead of the stored choice: the
 *  marketing site appends ?lang= to its sign-up and log-in links on every
 *  page that is not English, so somebody who just read a French page does not
 *  land on an English screen because his browser happens to be set to English.
 *  `caches` then writes it to localStorage, so the language survives the rest
 *  of the session without the parameter.
 *
 *  Exported so the test drives the shipped options instead of a copy of them:
 *  the marketing site now depends on this order, and a silent reshuffle here
 *  would send every non-English visitor to an English log-in screen again.
 *  An unknown code is matched against `supportedLngs` and falls back to `en`,
 *  so it never asks for a locale chunk that does not exist. */
export const detectionOptions = {
  order: ['querystring', 'localStorage', 'navigator'],
  lookupQuerystring: 'lang',
  lookupLocalStorage: 'priceq.lang',
  caches: ['localStorage'],
};

/** The tab title and `<html lang>` follow the language on screen. index.html
 *  can only carry one of each, so a German visitor read "en" to his screen
 *  reader and the tab never said anything in his language. Registered before
 *  init so the first language, not only a later switch, sets both. */
i18n.on('languageChanged', (lng) => {
  if (typeof document === 'undefined') return; // node-environment tests
  document.documentElement.lang = i18n.resolvedLanguage ?? lng;
  document.title = i18n.t('common.documentTitle');
});

/** Resolves once the active language (and fallback) is loaded. main.tsx waits
 *  on it alongside the session restore so the first paint has its strings. */
export const i18nReady: Promise<unknown> = i18n
  .use(LanguageDetector)
  .use(lazyBackend)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: ['de', 'en', 'fr', 'nl', 'pl'],
    // 'nl-NL' resolves to the nl bundle instead of asking for a nl-NL chunk.
    load: 'languageOnly',
    // Kick off the locale fetch synchronously at import time instead of on
    // the next tick — it runs in parallel with the session-restore request.
    initAsync: false,
    detection: detectionOptions,
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
